
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    FaultInjectIconSvg,
    OrbitalInsightsIconSvg,
    OrbitAnimationIcon,
    PresimIconSvg,
    ScheduleIconSvg,
    ThermalHeatmapSvg
} from '../../../../CommonComponents/svgs/Svgs'
import GroupButton from './GroupButton'
import './ScenarioButtonsGroups.scss'
import useReportWindow from '../../hooks/useReportWindow'
import useForm from '../../hooks/useForm'
import { SCENARIO_OPERATION_TYPES } from '../../../constants'
import useAssets from '../../hooks/useAssets'
import useSelectedGroupSatellite from '../../hooks/useSelectedGroupSatellite'
import usePresimApis from '../../hooks/usePresimApis'
import useNavigation from '../../hooks/useNavigation'
import { createPredictionOrbitPacket, handleFitToscreenMode } from '../../utils/viewer-packets'
import useViewer from '../../hooks/useViewer'
import { requestRender } from '../../../../../utils/cesiumHelperFunctions'
import { PlaySvg } from '../../../../CommonComponents/timeline/Svg'
import useScenarioResponse from '../../hooks/useScenarioResponse'
import useOrbitAnimationDetails from '../../hooks/useOrbitAnimationDetails'
import useThermalHeatmap from '../../hooks/useThermalHeatmap'
import { CircularProgress } from '@mui/material'
import { ModalKeys, WindowKeys } from '../../utils/scenario-types'

const ANIMATION_PREPARE_DELAY = 700

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const waitForNextPaint = () => new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame !== 'function') {
        setTimeout(resolve, 0)
        return
    }

    requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve())
    })
})

const ScenarioButtonsGroups = () => {
    const { formData } = useForm()
    const { postPresimData } = usePresimApis()
    const { viewer, updateViewerMode } = useViewer()
    const { missionId, scenarioId } = useNavigation()
    const { scenarioResponse } = useScenarioResponse()
    const { presim, schedule, orbitalInsights } = useAssets()
    const { selectedGroupSatellite } = useSelectedGroupSatellite()
    const { windowStore, openReportModal, getSortedOpenWindows } = useReportWindow()
    const { orbitAnimationDetails, setOrbitAnimationDetails, stopPresimAnimation } = useOrbitAnimationDetails()
    const { isThermalOpen, setIsThermalOpen } = useThermalHeatmap()

    const [animationPreparing, setAnimationPreparing] = useState(false)
    const animationActionInProgressRef = useRef(false)

    const startAnimation = useMemo(() => orbitAnimationDetails.startAnimation, [orbitAnimationDetails.startAnimation])
    const animationSpeed = useMemo(() => orbitAnimationDetails.animationSpeed, [orbitAnimationDetails.animationSpeed])

    const draftMode = useMemo(() => (scenarioResponse && scenarioResponse?.draftMode === 'No') ? false : true, [scenarioResponse])
    const scenarioOperation = useMemo(() => formData?.details?.scenarioOperation, [formData?.details?.scenarioOperation])

    const sameScenarioOperation = useMemo(() => scenarioOperation === scenarioResponse?.scenarioOperation, [scenarioOperation, scenarioResponse?.scenarioOperation])

    const sortedPinnedWindows = useMemo(() => getSortedOpenWindows(windowStore?.modals).map((window) => window.report), [windowStore?.modals])

    const activeOrbitalInsights = useMemo(() => windowStore?.modals?.orbitReport.open || sortedPinnedWindows?.includes('orbitReport'), [windowStore?.modals?.orbitReport.open, sortedPinnedWindows])
    const activeSchedule = useMemo(() => windowStore?.modals?.scheduleReport.open || sortedPinnedWindows?.includes('scheduleReport'), [windowStore?.modals?.scheduleReport.open, sortedPinnedWindows])
    const activePresim = useMemo(() => windowStore?.modals?.presimReport.open || sortedPinnedWindows?.includes('presimReport'), [windowStore?.modals?.presimReport.open, sortedPinnedWindows])
    const activeFaultplan = useMemo(() => windowStore?.modals?.faultReport.open || sortedPinnedWindows?.includes('faultReport'), [windowStore?.modals?.faultReport.open, sortedPinnedWindows])

    const fidelity = useMemo(() => presim.fidelityType, [presim.fidelityType])
    const selectedGroup = useMemo(() => selectedGroupSatellite?.groupId || '', [selectedGroupSatellite?.groupId])
    const selectedSatellite = useMemo(() => selectedGroupSatellite?.satelliteId || '', [selectedGroupSatellite?.satelliteId])
    const presimData = useMemo(() => presim?.data, [presim, presim?.data])
    const selectedPresimData = useMemo(() => presimData?.[selectedSatellite], [presimData, selectedSatellite])

    const presimStatus = useMemo(() => selectedPresimData ? selectedPresimData?.status : 'idle', [selectedPresimData, selectedSatellite])
    const presimOrbit = useMemo(() => selectedPresimData ? selectedPresimData.orbitDetails : null, [selectedPresimData, selectedSatellite])

    const hasRunningPresim = useMemo(() => presimData && Object.keys(presimData).some((key) => (presimData[key].status === 'running' && key !== selectedSatellite)), [presimData, selectedSatellite])
    const disabledPresimButton = useMemo(() => (hasRunningPresim && presimStatus !== 'completed') || presimStatus === 'running' || !selectedGroup || !selectedSatellite || schedule?.status !== 'completed', [presimStatus, hasRunningPresim, selectedGroup, selectedSatellite, schedule])

    const animationSvg = useMemo(() => animationPreparing ? <CircularProgress size={20} color="inherit" /> : startAnimation === selectedSatellite ? <PlaySvg /> : <OrbitAnimationIcon />, [animationPreparing, startAnimation, selectedSatellite])

    const toggleAnimationLoader = (state: boolean) => {
        setAnimationPreparing(state)
    }

    const onClick = useCallback(async (modalKey: ModalKeys, windowKey: WindowKeys) => {
        openReportModal(modalKey, windowKey)
    }, [openReportModal])

    const getScheduleIdofSelectedSatellite = () => {
        if (!schedule?.allSchedules || !selectedSatellite) return selectedSatellite
        for (const group of schedule.allSchedules) {
            const sat = group.satellites?.find((s: any) => s.satId === selectedSatellite)
            if (sat) return sat.scheduleId || sat.satId || selectedSatellite
        }
        return selectedSatellite
    }

    const onClickPresim = useCallback(async (modalKey: ModalKeys, windowKey: WindowKeys) => {
        if (presimStatus === 'completed') {
            openReportModal(modalKey, windowKey)
        } else {
            const scheduleId = getScheduleIdofSelectedSatellite()
            await postPresimData(missionId, scenarioId!, selectedGroup!, selectedSatellite!, fidelity, scheduleId)
        }
    }, [openReportModal, missionId, scenarioId, presimStatus, selectedGroup, selectedSatellite, postPresimData, fidelity])

    const setStartAnimation = useCallback((value: string | boolean) => {
        setOrbitAnimationDetails((prev) => ({
            ...prev,
            startAnimation: value,
            animationSpeed: 2,
            controllerState: value ? 'play' : 'pause',
            showAnimationDetails: value ? true : false
        }))
    }, [setOrbitAnimationDetails])

    const togglePredictionAnimation = useCallback(async () => {
        if (animationActionInProgressRef.current) return
        if (!selectedSatellite || !presimOrbit || !viewer || !missionId) return

        animationActionInProgressRef.current = true
        toggleAnimationLoader(true)

        try {
            await waitForNextPaint()

            if (startAnimation === selectedSatellite) {
                await stopPresimAnimation()
                return
            }
            await updateViewerMode('3D')
            await wait(ANIMATION_PREPARE_DELAY)
            await createPredictionOrbitPacket(viewer, scenarioId, missionId, {
                satelliteGroupId: selectedGroup,
                satelliteId: selectedSatellite,
                data: presimOrbit,
            }, animationSpeed)
            handleFitToscreenMode(viewer)
            setStartAnimation(selectedSatellite)
        } finally {
            toggleAnimationLoader(false)
            animationActionInProgressRef.current = false
            requestRender(viewer)
        }
    }, [selectedSatellite, presimOrbit, viewer, missionId, startAnimation, stopPresimAnimation, updateViewerMode, scenarioId, selectedGroup, animationSpeed, setStartAnimation])

    useEffect(() => {
        if (!viewer) return
        if (startAnimation && startAnimation !== selectedSatellite) {
            (async () => {
                await stopPresimAnimation()
            })()
        }
    }, [selectedSatellite, startAnimation, viewer, stopPresimAnimation])

    return (
        <div className='scenario__buttons__group__container'>
            <GroupButton
                id='orbital__insights__report__button'
                label='Orbital Insights'
                active={activeOrbitalInsights}
                className='modal__trigger__button'
                disabled={orbitalInsights?.status !== 'completed' || !sameScenarioOperation}
                onClick={() => onClick('orbitReport', 'exposure')}
            >
                <OrbitalInsightsIconSvg />
            </GroupButton>
            {scenarioOperation === SCENARIO_OPERATION_TYPES.CONOPS &&
                <GroupButton
                    id='schedule__report__button'
                    label='Schedule'
                    active={activeSchedule}
                    disabled={schedule?.status !== 'completed' || !sameScenarioOperation}
                    className='modal__trigger__button'
                    onClick={() => onClick('scheduleReport', 'schedule')}
                >
                    <ScheduleIconSvg />
                </GroupButton>
            }
            <GroupButton
                id='presim__report__button'
                label='Schedule Prediction'
                active={activePresim}
                className='modal__trigger__button'
                onClick={() => onClickPresim('presimReport', 'power')}
                disabled={disabledPresimButton || !sameScenarioOperation}
            >
                {presimStatus !== 'completed' && <span className='button__text'>{presimStatus === 'running' ? 'Analyzing' : 'Analyze'}</span>}
                {presimStatus !== 'running' && <PresimIconSvg />}
                {presimStatus === 'running' && <div className='spin__loader' />}
            </GroupButton>
            {scenarioOperation === SCENARIO_OPERATION_TYPES.CONOPS &&
                <GroupButton label='Play Animation'
                    active={startAnimation === selectedSatellite}
                    disabled={presimStatus !== 'completed' || disabledPresimButton || animationPreparing || !sameScenarioOperation}
                    onClick={togglePredictionAnimation}>
                    {animationSvg}
                </GroupButton>
            }
            <GroupButton
                label='Thermal Analysis'
                active={isThermalOpen}
                disabled={presimStatus !== 'completed' || disabledPresimButton || animationPreparing || !sameScenarioOperation}
                onClick={() => setIsThermalOpen((v) => !v)}
            >
                <ThermalHeatmapSvg isActive={isThermalOpen} />
            </GroupButton>
            <GroupButton
                id='fault__report__button'
                label='Fault Schedule'
                disabled={draftMode || schedule?.status !== 'completed' || !sameScenarioOperation}
                active={activeFaultplan}
                className='modal__trigger__button'
                onClick={() => onClick('faultReport', 'faults')}
            >
                <FaultInjectIconSvg />
            </GroupButton>
        </div>
    )
}

export default ScenarioButtonsGroups
